import { PaperData } from '../types/paper';

export const seedQuantumKeyDistribution: PaperData = {
  id: 'topic-quantum-qkd-shor',
  title: 'Quantum Key Distribution & Shor Algorithm',
  authors: ['Peter Shor', 'Charles Bennett', 'Gilles Brassard', 'Artur Ekert'],
  year: '1994',
  arxivId: 'Quantum Cryptography',
  abstract: 'Shor\'s algorithm provides exponential quantum speedup for integer factorization, threatening classical RSA public-key cryptography. In response, Quantum Key Distribution (QKD) protocols like BB84 leverage quantum mechanics (no-cloning theorem and Heisenberg uncertainty principle) to achieve information-theoretic security.',
  
  lineageGraph: {
    nodes: [
      {
        id: 'rsa_1977',
        label: 'RSA Cryptography',
        type: 'ancestor',
        year: '1977',
        authors: 'Rivest, Shamir, Adleman',
        description: 'Classical public-key cryptography relying on the computational difficulty of factoring prime products.',
        keyInnovation: 'Trapdoor one-way prime factorization.'
      },
      {
        id: 'heisenberg_1927',
        label: 'Heisenberg Uncertainty Principle',
        type: 'ancestor',
        year: '1927',
        authors: 'Werner Heisenberg',
        description: 'Fundamental quantum limit stating measuring non-commuting observables inevitably disturbs the quantum state.',
        keyInnovation: 'Irreducible quantum measurement disturbance.'
      },
      {
        id: 'bb84_1984',
        label: 'BB84 QKD Protocol',
        type: 'target_paper',
        year: '1984',
        authors: 'Bennett & Brassard',
        description: 'First quantum key distribution protocol using single-photon polarization states in non-orthogonal bases.',
        keyInnovation: 'Information-theoretic key exchange via physical quantum states.'
      },
      {
        id: 'e91_1991',
        label: 'E91 Entanglement QKD',
        type: 'descendant',
        year: '1991',
        authors: 'Artur Ekert',
        description: 'QKD protocol using entangled EPR photon pairs and Bell inequality violation tests to detect eavesdropping.',
        keyInnovation: 'Entanglement-based eavesdropping verification.'
      },
      {
        id: 'shor_1994',
        label: "Shor's Factoring Algorithm",
        type: 'target_paper',
        year: '1994',
        authors: 'Peter Shor',
        description: 'Quantum polynomial-time algorithm $O((\\log N)^3)$ for integer factorization using Quantum Fourier Transform.',
        keyInnovation: 'Exponential quantum speedup for period finding.'
      },
      {
        id: 'pqc_lattice',
        label: 'Post-Quantum Lattice Cryptography',
        type: 'descendant',
        year: '1996',
        authors: 'Ajtai, Regev et al.',
        description: 'Classical cryptographic primitives based on Learning With Errors (LWE) hard lattice problems resistant to Shor\'s algorithm.',
        keyInnovation: 'Quantum-resistant high-dimensional lattice hardness.'
      }
    ],
    links: [
      { source: 'heisenberg_1927', target: 'bb84_1984', label: 'Underpins state collapse' },
      { source: 'rsa_1977', target: 'shor_1994', label: 'Target of quantum factoring' },
      { source: 'bb84_1984', target: 'e91_1991', label: 'Evolved to entangled pairs' },
      { source: 'shor_1994', target: 'bb84_1984', label: 'Motivated quantum-safe key exchange' },
      { source: 'shor_1994', target: 'pqc_lattice', label: 'Drove post-quantum standardization' }
    ]
  },

  concepts: [
    {
      id: 'bb84_protocol',
      title: 'BB84 Protocol & Basis Sifting',
      category: 'core_innovation',
      definition: 'A quantum key distribution protocol where Alice transmits single photons in randomly chosen non-orthogonal bases (Computational Z or Diagonal X), allowing Alice and Bob to establish a shared secret key.',
      mathFormulas: [
        {
          label: 'Non-Orthogonal Quantum Bases',
          latex: '|\\psi\\rangle \\in \\{|0\\rangle, |1\\rangle, |+\\rangle, |-\\rangle\\}',
          explanation: 'Where $|+\\rangle = \\frac{|0\\rangle + |1\\rangle}{\\sqrt{2}}$ and $|-\\rangle = \\frac{|0\\rangle - |1\\rangle}{\\sqrt{2}}$.'
        },
        {
          label: 'Quantum Bit Error Rate (QBER)',
          latex: '\\text{QBER} = \\frac{N_{\\text{error}}}{N_{\\text{sifted}}}',
          explanation: 'Eavesdropping (Eve) measuring in an unaligned basis introduces an expected 25% error rate on sifted key bits.'
        }
      ],
      intuition: 'Alice transmits polarized photons. Bob measures them in a randomly chosen basis. When their basis choices match, Bob\'s measurement strictly matches Alice\'s. If Eve eavesdrops, Heisenberg\'s uncertainty principle causes state collapse, leaving detectable errors in the sifted key.',
      keyNuances: [
        'Information-theoretic security relies on physical laws, not computational hardness.',
        'Basis sifting over a public classical channel discards ~50% of transmitted bits.',
        'If QBER < 11%, error correction (Cascade) and privacy amplification yield a 100% secret key.'
      ],
      codeSnippet: {
        language: 'python',
        code: `from qiskit import QuantumCircuit
import numpy as np

def prepare_bb84_qubit(bit: int, basis: int) -> QuantumCircuit:
    qc = QuantumCircuit(1, 1)
    if bit == 1:
        qc.x(0)  # Flip to |1>
    if basis == 1: # 0 = Z basis, 1 = X basis
        qc.h(0)  # Rotate to |+> or |->
    return qc

# Alice prepares bit 1 in Diagonal basis (|-> state)
alice_circuit = prepare_bb84_qubit(bit=1, basis=1)`,
        explanation: 'Qiskit implementation showing single-qubit BB84 state preparation in computational and Hadamard bases.'
      },
      ancestors: [
        {
          paperTitle: 'Physical Review Letters',
          authorsYear: 'Heisenberg, 1927',
          concept: 'Uncertainty Principle',
          relationship: 'Measuring non-commuting quantum observables causes state collapse.'
        }
      ],
      descendants: [
        {
          concept: 'E91 Protocol',
          paperOrField: 'Ekert, 1991',
          impact: 'Replaced single photons with entangled EPR pairs.'
        }
      ],
      citations: ['Bennett & Brassard, 1984']
    },

    {
      id: 'shors_period_finding',
      title: "Shor's Quantum Period Finding",
      category: 'mathematical_formulation',
      definition: 'A quantum algorithm that factors large integers $N$ in polynomial time $O((\\log N)^3)$ by reducing factorization to order/period finding via Quantum Fourier Transform.',
      mathFormulas: [
        {
          label: 'Modular Order Function',
          latex: 'f(x) = a^x \\pmod N \\implies f(x + r) = f(x)',
          explanation: 'Finding the period $r$ such that $a^r \\equiv 1 \\pmod N$ allows factoring $N$.'
        },
        {
          label: 'Greatest Common Divisor Factors',
          latex: '\\gcd\\left(a^{r/2} \\pm 1, N\\right) \\in \\text{Non-trivial Factors of } N',
          explanation: 'If period $r$ is even and $a^{r/2} \\not\\equiv -1 \\pmod N$, $\\gcd$ reveals prime factors.'
        }
      ],
      intuition: 'Classically, checking every potential period takes exponential time. Shor\'s algorithm evaluates modular exponentiation in quantum superposition across all $x$, then uses quantum phase interference to concentrate probability amplitudes onto exact multiples of the reciprocal period $1/r$.',
      keyNuances: [
        'Polynomial complexity $O((\\log N)^3)$ breaks classical RSA, Diffie-Hellman, and Elliptic Curve Cryptography (ECC).',
        'Quantum Phase Estimation uses QFT to measure the period $r$ with high probability.'
      ],
      codeSnippet: {
        language: 'python',
        code: `import math

def extract_rsa_factors(a: int, r: int, N: int):
    # Verify period r is even
    if r % 2 != 0:
        return None
    half_mod = pow(a, r // 2, N)
    if half_mod == N - 1:
        return None
    factor1 = math.gcd(half_mod - 1, N)
    factor2 = math.gcd(half_mod + 1, N)
    return [g for g in (factor1, factor2) if 1 < g < N]

# Example: N = 15, a = 7, period r = 4
factors = extract_rsa_factors(a=7, r=4, N=15) # Returns [3, 5]`,
        explanation: 'Classical reduction step in Shor\'s algorithm converting quantum period $r$ into prime factors of $N$.'
      },
      ancestors: [
        {
          paperTitle: 'Communications of the ACM',
          authorsYear: 'Rivest et al., 1977',
          concept: 'RSA Factorization',
          relationship: 'Target classical primitive broken by quantum period finding.'
        }
      ],
      descendants: [
        {
          concept: 'Lattice-Based Post-Quantum Cryptography',
          paperOrField: 'NIST Standardization, 2022',
          impact: 'Replaced RSA with quantum-resistant CRYSTALS-Dilithium and Kyber.'
        }
      ],
      citations: ['Shor, 1994']
    }
  ],

  flashcards: [
    {
      id: 'fc-q1',
      conceptId: 'shors_period_finding',
      question: 'Why does classical RSA encryption become vulnerable in the presence of a scalable quantum computer?',
      answer: 'Shor\'s algorithm solves integer factorization in polynomial time $O((\\log N)^3)$ using quantum period finding, breaking the exponential computational hardness assumption that protects RSA keys.',
      formula: '\\text{Quantum Time: } O((\\log N)^3) \\quad \\text{vs. Classical: } O\\left(e^{c (\\log N)^{1/3}}\\right)',
      difficulty: 'hard',
      tags: ['Shor\'s Algorithm', 'RSA', 'Cryptography', 'Complexity']
    },
    {
      id: 'fc-q2',
      conceptId: 'bb84_protocol',
      question: 'What fundamental quantum principle prevents an eavesdropper (Eve) from secretly copying photons in BB84?',
      answer: 'The **Quantum No-Cloning Theorem**, which proves that an arbitrary unknown quantum state cannot be cloned identically by any unitary operation. Any attempt by Eve to measure or copy photons alters their state, creating detectable errors.',
      formula: 'U(|\\psi\\rangle|e\\rangle) \\neq |\\psi\\rangle|\\psi\\rangle',
      difficulty: 'hard',
      tags: ['QKD', 'No-Cloning', 'BB84', 'Quantum Physics']
    },
    {
      id: 'fc-q3',
      conceptId: 'bb84_protocol',
      question: 'In the BB84 protocol, what is the expected error rate introduced on intercepted key bits if Eve measures all transmitted photons in a randomly chosen basis?',
      answer: 'Eve chooses the wrong measurement basis 50% of the time. When her basis choice is wrong, Bob\'s subsequent measurement yields a wrong result 50% of the time. This results in an overall expected Quantum Bit Error Rate (QBER) of **25%** ($0.5 \\times 0.5$).',
      formula: '\\text{QBER}_{\\text{intercept}} = 0.5 \\times 0.5 = 25\\%',
      difficulty: 'medium',
      tags: ['BB84', 'QKD', 'QBER', 'Probability']
    },
    {
      id: 'fc-q4',
      conceptId: 'shors_period_finding',
      question: 'How does Shor\'s algorithm convert integer factorization into a period-finding problem?',
      answer: 'It constructs the modular function $f(x) = a^x \\pmod N$. Finding its periodic order $r$ guarantees $a^r - 1 \\equiv 0 \\pmod N$. Factoring $a^r - 1 = (a^{r/2} - 1)(a^{r/2} + 1)$ reveals non-trivial factors of $N$ via $\\gcd(a^{r/2} \\pm 1, N)$.',
      formula: '\\gcd\\left(a^{r/2} \\pm 1, N\\right)',
      difficulty: 'extreme',
      tags: ['Shor\'s Algorithm', 'Period Finding', 'Math Derivation']
    }
  ]
};
